import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

const STORAGE_KEY = 'vd-lista-popup-dismissed';

/**
 * Newsletter ("la Lista") popup. Opens once after `data-delay` seconds unless dismissed within the
 * last `data-dismiss-days` days. After a successful signup the page reloads with the form posted,
 * and the popup reopens immediately to show the confirmation and discount code.
 *
 * @extends {DialogComponent}
 */
class VdListaPopup extends DialogComponent {
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    // Theme editor: open only while this section is selected.
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:select', this.#handleEditorSelect);
      document.addEventListener('shopify:section:deselect', this.#handleEditorDeselect);
      return;
    }

    this.addEventListener(DialogCloseEvent.eventName, this.#remember);
    document.addEventListener('click', this.#handleListaLink);

    if (this.hasAttribute('data-show-now')) {
      this.showDialog();
      return;
    }
    if (this.dataset.autoOpen === 'false' || this.#recentlyDismissed()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.#timer = window.setTimeout(() => this.showDialog(), delay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#timer);
    this.removeEventListener(DialogCloseEvent.eventName, this.#remember);
    document.removeEventListener('click', this.#handleListaLink);
    document.removeEventListener('shopify:section:select', this.#handleEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.#handleEditorDeselect);
  }

  /** @param {Event & { detail?: { sectionId?: string } }} event */
  #handleEditorSelect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.showDialog();
  };

  /** @param {Event & { detail?: { sectionId?: string } }} event */
  #handleEditorDeselect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.closeDialog();
  };

  /** Any link to #lista opens the popup (e.g. the hero "Entra nella Lista" button). */
  #handleListaLink = (/** @type {MouseEvent} */ event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href="#lista"]');
    if (!link) return;
    event.preventDefault();
    window.clearTimeout(this.#timer);
    this.showDialog();
  };

  #remember = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private mode): the popup may show again, which is acceptable.
    }
  };

  /** @returns {boolean} */
  #recentlyDismissed() {
    const days = Number(this.dataset.dismissDays) || 0;
    try {
      const last = Number(localStorage.getItem(STORAGE_KEY));
      return Boolean(last) && Date.now() - last < days * 86_400_000;
    } catch {
      return false;
    }
  }
}

if (!customElements.get('vd-lista-popup')) customElements.define('vd-lista-popup', VdListaPopup);
